import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { usePagination } from '../../hooks/usePagination'
import { userService } from '../../services/userService'
import { AdminUser } from '../../types/auth'
import { Button } from '../../components/ui/Button'
import { Pagination } from '../../components/ui/Pagination'
import { UserRow } from './UserRow'
import { useUserActions } from './useUserActions'
import {
  PageHeader,
  PageMeta,
  PageEyebrow,
  PageTitle,
  TableWrapper,
  StyledTable,
  THead,
  TH,
  EmptyState,
  TableSkeleton,
} from '../../components/ui/Table'

export function UsersPage() {
  const { token } = useAuth()
  const svc = useMemo(() => userService(token), [token])
  const { page, perPage, setPage } = usePagination()

  const [items, setItems] = useState<AdminUser[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  // ── Load ──────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await svc.list(page, perPage)
      setItems(res.items)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [page, perPage, svc])

  useEffect(() => {
    load()
  }, [load])

  // Edit / reset password / integrations / support / deactivate — shared with UserDetailPage
  const { actions, dialogs } = useUserActions(svc, load)

  const handleAddClick = useCallback(() => actions.openEdit(), [actions])

  return (
    <>
      <PageHeader>
        <PageMeta>
          <PageEyebrow>System</PageEyebrow>
          <PageTitle>Users</PageTitle>
        </PageMeta>
        <Button onClick={handleAddClick}>+ Add user</Button>
      </PageHeader>

      {loading ? (
        <TableSkeleton cols={7} />
      ) : (
        <>
          <TableWrapper>
            <StyledTable>
              <THead>
                <tr>
                  <TH>User</TH>
                  <TH>Plan</TH>
                  <TH>Role</TH>
                  <TH>Status</TH>
                  <TH>Integrations</TH>
                  <TH>Last login</TH>
                  <TH />
                </tr>
              </THead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState message="No users found." />
                    </td>
                  </tr>
                )}
                {items.map(item => (
                  <UserRow key={item.id} item={item} actions={actions} />
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
          <Pagination page={page} total={total} perPage={perPage} onChange={setPage} />
        </>
      )}

      {dialogs}
    </>
  )
}
